
import React, { useState, ReactNode, cloneElement } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    TextField
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses'
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {SxProps, Theme, useTheme} from '@mui/material/styles';
import {IconJSON,
        IconModel,
        LayoutFieldModel} from '../../models/models';
import {validateEmailField} from '../../utils/fieldValidations';
import {checkLayoutFieldRequirement} from '../../utils/fieldValidations';
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaidIcon from "@mui/icons-material/Paid";
import CancelIcon from "@mui/icons-material/Cancel";
import GroupIcon from "@mui/icons-material/Group";
import PaymentsIcon from "@mui/icons-material/Payments";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import AttachmentIcon from "@mui/icons-material/Attachment";
import PhoneIcon from "@mui/icons-material/Phone";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import EuroIcon from "@mui/icons-material/Euro";
import TopicIcon from "@mui/icons-material/Topic";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CloudIcon from "@mui/icons-material/Cloud";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import AddIcon from "@mui/icons-material/Add";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import CompareIcon from "@mui/icons-material/Compare";
import AddIcCallIcon from "@mui/icons-material/AddIcCall";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SyncIcon from "@mui/icons-material/Sync";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import UploadIcon from "@mui/icons-material/Upload";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import PolicyIcon from "@mui/icons-material/Policy";
import ArticleIcon from "@mui/icons-material/Article";

import IconGreenfield from "../../resources/icons/icon_greenfield.png";
import IconBlocked from "../../resources/icons/icon_blocked.png";
import IconNamed from "../../resources/icons/icon_named.png";
import {SvgIconProps} from '@mui/material/SvgIcon';

/*DEFINITION FOR IMAGE ICONS */

type ThemeColor = SvgIconProps['color'];
type CssColor = string;
type IconColor = ThemeColor | CssColor;

const isThemeColor = (color?: IconColor): color is ThemeColor =>
   color === 'inherit' ||
   color === 'primary' ||
   color === 'secondary' ||
   color === 'action' ||
   color === 'disabled' ||
   color === 'error' ||
   color === 'info' ||
   color === 'success' ||
   color === 'warning'

interface ImageIconProps extends SvgIconProps {
  src: string;
  alt?: string;
}

export const ImageIcon: React.FC<ImageIconProps> = ({
  src,
  alt,
  fontSize = 'medium',
  ...props
}) => {
  const sizeMap = {
    small: 20,
    medium: 24,
    large: 35,
    inherit: 24
  }

  const size = typeof fontSize === 'number'
     ? fontSize
     : sizeMap[fontSize]

  const moreStyles = {...props.sx}

  return(
    <img
       src={src}
       alt={alt}
       height={size}
       width={'auto'}
       style={{display: 'inline-block', ...moreStyles}}
    />
  )
}

/* STANDARD HAND OVER */

type IconComponent = React.ComponentType<SvgIconProps>;
const ICON_MAP: Record<string, IconComponent> = {
  'MuiIcon-PersonIcon': PersonIcon,
  'MuiIcon-AccountBalanceIcon': AccountBalanceIcon,
  'MuiIcon-PaidIcon': PaidIcon,
  'MuiIcon-CancelIcon': CancelIcon,
  'MuiIcon-GroupIcon': GroupIcon,
  'MuiIcon-EmailIcon': EmailIcon,
  'MuiIcon-CalendarTodayIcon': CalendarTodayIcon,
  'MuiIcon-PaymentsIcon': PaymentsIcon,
  'MuiIcon-LeaderboardIcon': LeaderboardIcon,
  'MuiIcon-AttachmentIcon': AttachmentIcon,
  'MuiIcon-PhoneIcon': PhoneIcon,
  'MuiIcon-EmojiEventsIcon': EmojiEventsIcon,
  'MuiIcon-HowToRegIcon': HowToRegIcon,
  'MuiIcon-CoPresentIcon': CoPresentIcon,
  'MuiIcon-EuroIcon': EuroIcon,
  'MuiIcon-TopicIcon': TopicIcon,
  'MuiIcon-MenuBookIcon': MenuBookIcon,
  'MuiIcon-TextSnippetIcon': TextSnippetIcon,
  'MuiIcon-AttachMoneyIcon': AttachMoneyIcon,
  'MuiIcon-SettingsIcon': SettingsIcon,
  'MuiIcon-AssessmentIcon': AssessmentIcon,
  'MuiIcon-AccountBoxIcon': AccountBoxIcon,
  'MuiIcon-CloudIcon': CloudIcon,
  'MuiIcon-AirportShuttleIcon': AirportShuttleIcon,
  'MuiIcon-ContactMailIcon': ContactMailIcon,
  'MuiIcon-AddIcon': AddIcon,
  'MuiIcon-TroubleshootIcon': TroubleshootIcon,
  'MuiIcon-EditCalendarIcon': EditCalendarIcon,
  'MuiIcon-CompareIcon': CompareIcon,
  'MuiIcon-AddIcCallIcon': AddIcCallIcon,
  'MuiIcon-AddCallIcon': AddIcCallIcon,
  'MuiIcon-PersonAddAlt1Icon': PersonAddAlt1Icon,
  'MuiIcon-SyncIcon': SyncIcon,
  'MuiIcon-ScheduleSendIcon': ScheduleSendIcon,
  'MuiIcon-UploadIcon': UploadIcon,
  'MuiIcon-CompareArrowsIcon': CompareArrowsIcon,
  'MuiIcon-SupportAgentIcon': SupportAgentIcon,
  'MuiIcon-ShoppingCartIcon': ShoppingCartIcon,
  'MuiIcon-InventoryIcon': InventoryIcon,
  'MuiIcon-PolicyIcon': PolicyIcon,
  'MuiIcon-ArticleIcon': ArticleIcon,
  'AssetIcon-IconGreenfield': (props) => <ImageIcon {...props} src={IconGreenfield} alt='Greenfield Account' />,
  'AssetIcon-IconBlocked': (props) => <ImageIcon {...props} src={IconBlocked} alt='Blocked Account' />,
  'AssetIcon-IconNamed': (props) => <ImageIcon {...props} src={IconNamed} alt='Named Account' />
}


export const CustomIconComponent: React.FC<{icon: IconJSON | IconModel, sx?: SxProps<Theme>}> = ({icon, sx}) => {
  const thisKey = `${icon.Type_sys}-${icon.Name_sys}`;
//  const thisKey = 'AssetIcon-IconGreenfield';
  const thisElement = ICON_MAP[thisKey];

  if(!thisElement) return null;

  //default sx
  const thisSX = {...sx,
                  color: 'default'}

  const thisProps: SvgIconProps = {}
  if(isThemeColor(icon.Colour_sys)){
    thisProps.color = icon.Colour_sys;
  } else{
    thisSX.color = icon.Colour_sys
  }

  const Icon = ICON_MAP[thisKey];

  return <Icon {...thisProps} sx={thisSX}/>;


};
